import { useState, useEffect } from "react";
import { ArrowLeft, } from "lucide-react";
import { showError, showSuccess, showSeoWarnings } from "../lib/toast";
import {
  server_post_data,
  blog_save_update,
  get_blog_data,
} from "../serviceconnection/ServiceConnection";
import { MediaImageField } from "../components/MediaPickerDialog";
import RichTextEditor from "../components/RichTextEditor";
// ── Bug Fix 1: image_name added, Bug Fix 2: object (not array) ───────────────
const EMPTY_FORM = {
  p_id: 0,
  title_name: "",
  tag_line: "",
  description: "",
  author: "",
  designation: "",
  read_time: "",
  category: "",
  image_name: "", // ← was missing in original
  seo_id: 0,
  actual_url: "",
  link_url: "",
  canonical_url: "",
  title_name_seo: "",
  description_seo: "",
  keywords: "",
  pretty_function_name: "",
  call_function_name: "",
  content_type: "article",
  changefreq: "monthly",
  priority_screen: "0.7",
};

// ─────────────────────────────────────────────────────────────────────────────
// Props:
//   editId  — null (add) | number (edit)
//   onBack  — back without saving
//   onSaved — after successful save
// ─────────────────────────────────────────────────────────────────────────────
export default function BlogForm({ editId, onBack, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM); // ← Bug Fix 2: was useState([])
  const [activeTab, setActiveTab] = useState("content");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // ── Load existing data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        setFetching(true);
        const fd = new FormData();
        fd.append("main_id", editId);
        const res = await server_post_data(get_blog_data, fd);
        const d = res.data?.data;
        if (!d) {
          showError("Failed to load blog");
          return;
        }
        const seo = d.seo_data ?? {};
        setForm({
          p_id: d.primary_id,
          title_name: d.title_name ?? "",
          tag_line: d.tag_line ?? "",
          description: d.description ?? "",
          author: d.author ?? "",
          designation: d.designation ?? "",
          read_time: d.read_time ?? "",
          category: d.category ?? "",
          image_name: d.image_name ?? "",
          seo_id: d.seo_id ?? 0,
          actual_url: seo.actual_url ?? "",
          link_url: seo.link_url ?? "",
          canonical_url: seo.canonical_url ?? "",
          title_name_seo: seo.title_name ?? "",
          description_seo: seo.description ?? "",
          keywords: seo.keywords ?? "",
          pretty_function_name: seo.pretty_function_name ?? "",
          call_function_name: seo.call_function_name ?? "",
          content_type: seo.content_type ?? "article",
          changefreq: seo.changefreq ?? "monthly",
          priority_screen: seo.priority_screen ?? "0.7",
        });
      } catch {
        showError("Failed to load blog details");
      } finally {
        setFetching(false);
      }
    })();
  }, [editId]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.title_name || !form.description || !form.author) {
      showError("Title, Description and Author are required");
      return;
    }
    if (!form.image_name) {
      showError("Cover image URL is required");
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await server_post_data(blog_save_update, fd);
      if (res.data?.error) {
        showError(res.data?.message || "Save failed");
        return;
      }
      showSuccess(form.p_id > 0 ? "Blog updated!" : "Blog published!");
      showSeoWarnings(res);
      onSaved();
    } catch {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (fetching) {
    return (
      <div className="max-w-[800px] mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="page-title">Loading...</h1>
        </div>
        <div className="section-panel p-10 text-center text-muted-foreground text-sm">
          Fetching blog data...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[800px] mx-auto space-y-5">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted border border-border text-muted-foreground"
            title="Back to list"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {form.p_id > 0 ? "Edit Blog" : "Write New Blog"}
            </h1>
            <p className="page-subtitle">
              {form.p_id > 0
                ? `Editing: ${form.title_name || "Untitled"}`
                : "Fill in the details to publish a new blog"}
            </p>
          </div>
        </div>
        {/* Save button in header */}
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          {loading
            ? "Saving..."
            : form.p_id > 0
              ? "Update Blog"
              : "Publish Blog"}
        </button>
      </div>

      {/* ── Tabs + Form ── */}
      <div className="section-panel p-0">
        <div className="flex border-b border-border px-5">
          {[
            { key: "content", label: "Blog Content" },
            { key: "seo", label: "SEO Settings" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-3 text-xs font-semibold border-b-2 transition-colors -mb-px ${
                activeTab === t.key
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="section-panel-body space-y-5">
          {/* ══ CONTENT TAB ══ */}
          {activeTab === "content" && (
            <>
              {/* Cover Image URL */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  Cover Image <span className="text-destructive">*</span>
                </label>
                <MediaImageField
                  label="Select cover image"
                  hint="Recommended: 1200×630px"
                  value={form.image_name}
                  onChange={(v) => setForm((p) => ({ ...p, image_name: v }))}
                />
              </div>

              {/* Title */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  Blog Title <span className="text-destructive">*</span>
                </label>
                <input
                  name="title_name"
                  value={form.title_name}
                  onChange={handleChange}
                  placeholder="e.g. Complete Guide to SAP Business One"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                />
              </div>

              {/* Tag Line */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  Tag Line / Subtitle
                </label>
                <input
                  name="tag_line"
                  value={form.tag_line}
                  onChange={handleChange}
                  placeholder="A short description shown on listing page"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                />
              </div>

              {/* Description — CKEditor */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  Blog Content <span className="text-destructive">*</span>
                </label>
                <div className="rounded-lg border border-border overflow-hidden focus-within:border-accent [&_.ck-editor__editable]:min-h-[280px] [&_.ck-editor__editable]:text-sm [&_.ck-toolbar]:border-b [&_.ck-toolbar]:border-border [&_.ck-toolbar]:bg-muted/40">
                  <RichTextEditor
                    value={form.description}
                    onChange={(data) =>
                      setForm((p) => ({ ...p, description: data }))
                    }
                    placeholder="Write your full blog content here..."
                  />
                </div>
              </div>

              {/* Author + Designation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Author <span className="text-destructive">*</span>
                  </label>
                  <input
                    name="author"
                    value={form.author}
                    onChange={handleChange}
                    placeholder="e.g. Rahul Sharma"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Author Designation
                  </label>
                  <input
                    name="designation"
                    value={form.designation}
                    onChange={handleChange}
                    placeholder="e.g. SAP Consultant"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                  />
                </div>
              </div>

              {/* Category + Read Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Category
                  </label>
                  <input
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    placeholder="e.g. SAP B1, ERP, Implementation"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Read Time
                  </label>
                  <input
                    name="read_time"
                    value={form.read_time}
                    onChange={handleChange}
                    placeholder="e.g. 5 min read"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                  />
                </div>
              </div>
            </>
          )}

          {/* ══ SEO TAB ══ */}
          {activeTab === "seo" && (
            <>
              <p className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40 border border-border">
                Leave blank to auto-use blog title &amp; tag line as SEO title
                &amp; description.
              </p>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  SEO Title
                </label>
                <input
                  name="title_name_seo"
                  value={form.title_name_seo}
                  onChange={handleChange}
                  placeholder="SEO title (defaults to blog title)"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  Meta Description
                </label>
                <textarea
                  name="description_seo"
                  value={form.description_seo}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Meta description (defaults to tag line)"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent resize-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  Keywords
                </label>
                <input
                  name="keywords"
                  value={form.keywords}
                  onChange={handleChange}
                  placeholder="e.g. SAP, ERP, Business One, Implementation"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Change Frequency
                  </label>
                  <select
                    name="changefreq"
                    value={form.changefreq}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                  >
                    {[
                      "always",
                      "hourly",
                      "daily",
                      "weekly",
                      "monthly",
                      "yearly",
                      "never",
                    ].map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Priority (0.0 – 1.0)
                  </label>
                  <input
                    name="priority_screen"
                    value={form.priority_screen}
                    onChange={handleChange}
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    placeholder="0.7"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                  />
                </div>
              </div>
            </>
          )}

          {/* ── Footer Actions ── */}
          <div className="flex justify-end gap-3 pt-2 border-t border-border">
            <button
              onClick={onBack}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60 transition-colors"
              style={{ background: "hsl(var(--accent))" }}
            >
              {loading
                ? "Saving..."
                : form.p_id > 0
                  ? "Update Blog"
                  : "Publish Blog"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
